import React, { useState, useEffect } from 'react';
import { Clock, Users, Check, AlertCircle } from 'lucide-react';
import { Modal, Button, Input } from '../ui';
import { useJornadas } from '../../hooks/useJornadas';
import { useJugadoresEquipoActivo } from '../../hooks/useJugadoresEquipoActivo';
import { JornadaDto, JugadorDto } from '../../types';
import EstadisticasService from '../../services/EstadisticasService';

interface ModalMinutajeProps {
  jornada: JornadaDto;
  jugadores: JugadorDto[];
  onClose: () => void;
  onDataUpdated?: () => void;
}

/**
 * Modal para registrar minutaje de jugadores en una jornada
 */
export const ModalMinutaje: React.FC<ModalMinutajeProps> = ({ 
  jornada, 
  jugadores,
  onClose,
  onDataUpdated
}) => {
  // Obtener equipo desde el hook
  const { equipo } = useJugadoresEquipoActivo();
  
  const {
    jugadoresMinutaje,
    loading,
    error,
    guardando,
    seleccionarJornada,
    actualizarJugadorMinutaje,
    validarTitulares,
    guardarMinutaje,
    resetearModal,
    equipoRivalInput,
    actualizandoRival,
    actualizarEquipoRival
  } = useJornadas(jugadores, equipo); // Usar jugadores de props y equipo del hook

  const [errorLocal, setErrorLocal] = useState<string | null>(null);
  const [rivalTemporal, setRivalTemporal] = useState<string>('');

  // Cargar datos de la jornada al abrir el modal (optimizado)
  useEffect(() => {
    // Solo ejecutar una vez al abrir el modal
    if (jornada && jugadores.length > 0) {
      seleccionarJornada(jornada, jugadores);
    }
    
    // Cleanup solo cuando se cierre el modal (jornada se vuelve null)
    return () => {
      if (!jornada) {
        resetearModal();
      }
    };
  }, []); // Sin dependencias - solo se ejecuta al montar el componente

  /**
   * Maneja el cambio de selección de un jugador
   */
  const handleToggleJugador = (jugadorId: number, seleccionado: boolean) => {
    actualizarJugadorMinutaje(jugadorId, { 
      seleccionado,
      minutos: seleccionado ? 1 : 0,
      titular: seleccionado ? false : false
    });
    setErrorLocal(null);
  };

  const maxMinutos = EstadisticasService.obtenerDuracionPartido(equipo?.nombre || '');

  /**
   * Maneja el cambio de minutos de un jugador
   */
  const handleCambiarMinutos = (jugadorId: number, minutos: number) => {
    if (minutos < 0 || minutos > maxMinutos) return;
    
    actualizarJugadorMinutaje(jugadorId, { minutos });
    setErrorLocal(null);
  };

  /**
   * Maneja el cambio de titularidad de un jugador
   */
  const handleToggleTitular = (jugadorId: number, titular: boolean) => {
    if (titular && !validarTitulares(jugadorId, titular)) {
      return;
    }
    
    actualizarJugadorMinutaje(jugadorId, { titular });
    setErrorLocal(null);
  };

  /**
   * Maneja el guardado del minutaje y el rival
   */
  const handleGuardar = async () => {
    setErrorLocal(null);

    try {
      // Primero actualizar el rival si ha cambiado
      if (rivalTemporal !== equipoRivalInput) {
        const rivalActualizado = await actualizarEquipoRival(rivalTemporal);
        if (!rivalActualizado) {
          setErrorLocal('Error al actualizar el equipo rival');
          return;
        }
      }

      const exito = await guardarMinutaje();
      
      if (exito) {
        // Notificar al componente padre que los datos se actualizaron
        onDataUpdated?.();
        onClose();
      } else {
        setErrorLocal('Error al guardar el minutaje');
      }
    } catch (err) {
      setErrorLocal('Error al guardar los datos');
    }
  };

  // Inicializar el valor temporal del rival cuando se carga la jornada
  useEffect(() => {
    if (equipoRivalInput !== undefined) {
      setRivalTemporal(equipoRivalInput);
    }
  }, [equipoRivalInput]);

  /**
   * Cuenta los titulares seleccionados y determina el límite
   */
  const titularesSeleccionados = jugadoresMinutaje.filter(j => j.titular).length;
  const esFutbol11 = equipo?.seccion?.includes('F11');
  const maxTitulares = esFutbol11 ? 11 : 7;

  return (
    <Modal isOpen={true} onClose={onClose} size="lg">
      <div className="bg-secondary-800 rounded-xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-secondary-700">
          <div className="flex items-center space-x-3">
            <div className="bg-primary-600 rounded-full p-1.5">
              <Clock className="w-4 h-4 text-white" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-white">
                Minutaje - Jornada {jornada.numero}
              </h2>
              {/* Input para el equipo rival */}
              <div className="mt-2">
                <Input
                  type="text"
                  value={rivalTemporal}
                  onChange={(e) => setRivalTemporal(e.target.value)}
                  placeholder="Nombre del equipo rival"
                  disabled={actualizandoRival}
                  className="text-sm"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Información de titulares */}
        <div className="px-6 py-3 bg-secondary-900 border-b border-secondary-700">
          <div className="flex items-center justify-center">
            <div className={`flex items-center space-x-2 px-4 py-2 rounded-full ${
              titularesSeleccionados > maxTitulares 
                ? 'bg-red-900/30 border border-red-600' 
                : 'bg-primary-900/30 border border-primary-600'
            }`}>
              <Users className={`w-5 h-5 ${
                titularesSeleccionados > maxTitulares ? 'text-red-400' : 'text-primary-400'
              }`} />
              <span className={`font-semibold ${
                titularesSeleccionados > maxTitulares ? 'text-red-400' : 'text-primary-400'
              }`}>
                Titulares: {titularesSeleccionados}/{maxTitulares}
              </span>
              {titularesSeleccionados > maxTitulares && (
                <AlertCircle className="w-4 h-4 text-red-400" />
              )}
            </div>
          </div>
        </div>

        {/* Lista de jugadores optimizada */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="text-secondary-400">Cargando jugadores...</div>
            </div>
          ) : (
            <div className="space-y-2">
              {jugadoresMinutaje.map((jugador) => (
                <div
                  key={jugador.id}
                  onClick={() => handleToggleJugador(jugador.id, !jugador.seleccionado)}
                  className={`p-4 rounded-lg border-2 transition-all duration-200 cursor-pointer ${
                    jugador.seleccionado
                      ? 'bg-primary-900/20 border-primary-600 shadow-lg'
                      : 'bg-secondary-700 border-secondary-600 hover:border-secondary-500'
                  }`}
                >
                  <div className="flex items-center justify-between w-full">
                    {/* Lado izquierdo: Checkbox + Nombre */}
                    <div className="flex items-center space-x-3 flex-1">
                      <div
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                          jugador.seleccionado
                            ? 'bg-primary-600 border-primary-600'
                            : 'border-secondary-500'
                        }`}
                      >
                        {jugador.seleccionado && (
                          <Check className="w-3 h-3 text-white" />
                        )}
                      </div>

                      <span className="text-white font-medium text-lg">
                        {jugador.nombre} {jugador.apellido}
                      </span>
                    </div>

                    {/* Lado derecho: Controles de minutaje */}
                    {jugador.seleccionado && (
                      <div className="flex items-center space-x-3" onClick={(e) => e.stopPropagation()}>
                        {/* Input de minutos estrecho */}
                        <div className="flex items-center space-x-1">
                          <span className="text-secondary-400 text-sm font-medium">Min:</span>
                          <input
                            type="number"
                            min="1"
                            max={maxMinutos.toString()}
                            value={jugador.minutos || ''}
                            onChange={(e) => handleCambiarMinutos(jugador.id, parseInt(e.target.value) || 0)}
                            className="w-12 h-8 text-center text-secondary-50 bg-secondary-700 border-0 rounded-lg placeholder-secondary-400 focus:outline-none focus:ring-4 focus:ring-primary-500 transition-all duration-200 ease-in-out hover:bg-secondary-600 text-sm font-semibold"
                            placeholder="0"
                          />
                        </div>

                        {/* Botón T para titular */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleTitular(jugador.id, !jugador.titular);
                          }}
                          className={`w-7 h-7 rounded-lg border-2 flex items-center justify-center font-bold text-sm transition-all duration-200 ${
                            jugador.titular
                              ? 'bg-yellow-500 border-yellow-500 text-black shadow-lg transform scale-110'
                              : 'border-secondary-500 text-secondary-400 hover:border-yellow-500 hover:text-yellow-500'
                          }`}
                          title={jugador.titular ? 'Quitar de titulares' : 'Marcar como titular'}
                        >
                          T
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer con errores y botones */}
        <div className="p-6 border-t border-secondary-700">
          {(error || errorLocal) && (
            <div className="mb-4 p-3 bg-red-900/20 border border-red-600 rounded-lg">
              <div className="flex items-center space-x-2">
                <AlertCircle className="w-4 h-4 text-red-400" />
                <span className="text-red-400 text-sm">{error || errorLocal}</span>
              </div>
            </div>
          )}

          <div className="flex items-center justify-center space-x-3">
            <Button
              variant="secondary"
              size="sm"
              onClick={onClose}
              disabled={guardando}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleGuardar}
              disabled={guardando || loading}
            >
              <div className="flex items-center space-x-2">
                {guardando ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Guardando...</span>
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    <span>Guardar</span>
                  </>
                )}
              </div>
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ModalMinutaje;